import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { EMPTY, catchError, map, switchMap, tap } from 'rxjs'

import { ConversationApiService } from '@flowx/designer/modules/ai/common/api/conversation-api.service'
import { ConversationThreadExecutionPlanActionState } from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { AiConversationService } from '@flowx/designer/modules/ai/conversation/ai-conversation.service'
import { ConversationErrorActions } from '@flowx/designer/modules/ai/conversation/conversation-error.actions'
import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'

import { ConversationSelectors } from './conversation.selectors'
import { ExecutionPlanActions } from './execution-plan.actions'

import { ToastMessageService } from '../../../shared/services/toast-message.service'

@Injectable()
export class ConversationErrorEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiConversationService = inject(AiConversationService)
  #conversationApiService = inject(ConversationApiService)
  #toastMessageService = inject(ToastMessageService)

  storeErrorMessage = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(
          ConversationErrorActions.intentUnderstandingFailed,
          ConversationErrorActions.executionPlanImplementActionFailed,
          ConversationErrorActions.agentMessageFailed,
          ConversationErrorActions.agentOutcomeParsingFailed,
          ConversationErrorActions.keepOutcomeFailed,
          ConversationErrorActions.executionPlanUpdateFailed,
          ConversationErrorActions.simpleThreadFailed,
          ConversationErrorActions.actionPatchFailed,
          ConversationErrorActions.generateUiFromNodeActionFailed
        ),
        switchMap(() =>
          this.#aiConversationService.storeErrorMessage(
            'Seems like something was wrong with your request. I was not able to complete your task.'
          )
        )
      ),
    { dispatch: false }
  )

  addToastErrorMessage = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationErrorActions.conversationCreationFailed),
        tap(() => {
          this.#toastMessageService.showErrorMessage(
            'Seems like something was wrong with your request. I was not able to complete your task.',
            null,
            {
              appendTo: 'body',
            }
          )
        })
      ),
    { dispatch: false }
  )

  conversationLoadFailed = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationErrorActions.conversationLoadFailed),
        tap(() => {
          this.#toastMessageService.showErrorMessage(
            'The conversation could not be restored. Please try again.',
            null,
            {
              appendTo: 'body',
            }
          )
        })
      ),
    { dispatch: false }
  )

  fileUploadFailed = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationErrorActions.fileUploadFailed),
        tap(() => {
          this.#toastMessageService.showErrorMessage(
            'Seems like something was wrong with your request. I was not able to complete your task.',
            null,
            {
              appendTo: 'body',
            }
          )
        })
      ),
    { dispatch: false }
  )

  updateDataModelFailed = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationErrorActions.updateDataModelFailed),
        tap(() => {
          this.#toastMessageService.showErrorMessage(
            'Failed to save data model changes. Please try again.',
            null,
            {
              appendTo: 'body',
            }
          )
        })
      ),
    { dispatch: false }
  )

  discardActiveThreadOnError = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        ConversationErrorActions.executionPlanImplementActionFailed,
        ConversationErrorActions.agentMessageFailed,
        ConversationErrorActions.agentOutcomeParsingFailed,
        ConversationErrorActions.keepOutcomeFailed
      ),
      switchMap(() => {
        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()
        const messageWithOutcome = this.#store.selectSignal(
          ConversationSelectors.latestAgentMessageWithAgentAction
        )()
        if (!conversationId || !threadId) {
          return EMPTY
        }

        return this.#conversationApiService
          .discardThread(conversationId, threadId, {
            messageId: messageWithOutcome?.id,
            shouldFinalize: true,
          })
          .pipe(
            map(() => ConversationActions.discardThreadApiResponse({ conversationId, threadId })),
            catchError((err) => {
              console.error('[Conversation Error] Failed to discard thread after error:', err)
              return EMPTY
            })
          )
      })
    )
  )

  revertActionToPendingOnError = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        ConversationErrorActions.executionPlanImplementActionFailed,
        ConversationErrorActions.keepOutcomeFailed
      ),
      switchMap(({ conversationId }) => {
        const defaultThread = this.#store.selectSignal(ConversationSelectors.defaultThread)()

        if (!defaultThread) {
          return EMPTY
        }
        const messageWithPlan = this.#store.selectSignal(
          ConversationSelectors.lastDefaultThreadMessageWithValidPlan
        )()

        const executingAction = messageWithPlan?.executionPlan?.actions?.find(
          (a) => a.state === ConversationThreadExecutionPlanActionState.Executing
        )
        if (!executingAction) {
          return EMPTY
        }

        return this.#conversationApiService
          .patchPlanAction(
            conversationId,
            defaultThread.id,
            messageWithPlan.id,
            executingAction.localId,
            {
              state: ConversationThreadExecutionPlanActionState.Pending,
              context: { items: [] },
            }
          )
          .pipe(
            map(() =>
              ExecutionPlanActions.patchExecutionPlanActionState({
                actionId: executingAction.localId,
                actionState: ConversationThreadExecutionPlanActionState.Pending,
                messageId: messageWithPlan.id,
              })
            ),
            catchError((err) => {
              console.error('[Conversation Error] Failed to revert action to pending:', err)
              return EMPTY
            })
          )
      })
    )
  )
}
